import {
  classifySensitiveFormControl,
  identifyFormControlField,
  type IdentityFormControl,
} from "./identity-fields";
import type { ConsentSnapshot } from "./types";

type Enquiry = {
  receiptKey: string;
  sourcePath: string;
  fields: Record<string, string>;
};
type Pending = { body: Enquiry; expiresAt: number };
const MAX_ENQUIRY_BODY_BYTES = 32768;

export type EnquiryAttributionSnapshot = {
  apiBase: string;
  siteKey: string;
  anonId: string;
  sessionId: string;
  consentGeneration: number;
  identityRevocationGeneration: number;
  firstPartyFenceGeneration: number;
  configurationGeneration: number;
  sharedRevocationToken: string | null;
};
export type EnquiryAttributionContext = {
  occurredAt: number;
  visitor: { anonId: string; sessionId?: string };
  consent: Omit<ConsentSnapshot, "cmp"> & {
    cmp?: Omit<NonNullable<ConsentSnapshot["cmp"]>, "raw">;
  };
};

/** Bind to an already permitted browser context; never create or persist anchors. */
export function prepareEnquiryAttribution(options: {
  snapshot: () => EnquiryAttributionSnapshot | undefined;
  context: (snapshot: EnquiryAttributionSnapshot) => EnquiryAttributionContext;
}): (() => EnquiryAttributionContext | undefined) | undefined {
  const snapshot = options.snapshot();
  if (!snapshot) return undefined;
  const staged = { ...snapshot };
  const context = options.context(staged);
  const cmp = context.consent.cmp;
  const purposes = cmp?.purposes;
  // Snapshot only the bounded identity-envelope fields, never raw CMP data or
  // the local consent/transport generation counters used by the delivery guard.
  const serialized = JSON.stringify({
    occurredAt: context.occurredAt,
    visitor: {
      anonId: context.visitor.anonId,
      sessionId: context.visitor.sessionId,
    },
    consent: {
      state: context.consent.state,
      gpc: context.consent.gpc,
      dnt: context.consent.dnt,
      ...(cmp
        ? {
            cmp: {
              source: cmp.source,
              tcString: cmp.tcString,
              uspString: cmp.uspString,
              gppString: cmp.gppString,
              gppSectionIds: cmp.gppSectionIds,
              ...(cmp.source === "prospix_test_enrollment_v1"
                ? {
                    testEnrollmentToken: cmp.testEnrollmentToken,
                    testEnrollmentBrowserId: cmp.testEnrollmentBrowserId,
                    testEnrollmentExpiresAt: cmp.testEnrollmentExpiresAt,
                  }
                : {}),
              ...(purposes
                ? {
                    purposes: {
                      version: purposes.version,
                      analytics: purposes.analytics,
                      marketing: purposes.marketing,
                      ...(purposes.tcf
                        ? {
                            tcf: {
                              "1": purposes.tcf["1"],
                              "3": purposes.tcf["3"],
                              "4": purposes.tcf["4"],
                            },
                          }
                        : {}),
                    },
                  }
                : {}),
            },
          }
        : {}),
    },
  });
  let invalidated = false;
  return () => {
    if (invalidated) return undefined;
    const current = options.snapshot();
    if (
      !current ||
      !(Object.keys(staged) as Array<keyof EnquiryAttributionSnapshot>).every(
        (key) => current[key] === staged[key],
      )
    ) {
      invalidated = true;
      return undefined;
    }
    // Return a fresh copy so no caller can change the original submit evidence.
    return JSON.parse(serialized) as EnquiryAttributionContext;
  };
}
const businessFields = [
  "fullName",
  "email",
  "phone",
  "company",
  "website",
  "message",
  "availability",
  "timeZone",
  "contactMethod",
  "plan",
  "businessDescription",
  "teamType",
  "monthlySessions",
  "monthlyOrderVolume",
  "currentTools",
  "primaryUseCase",
  "decisionHelp",
  "canJoinFeedbackCalls",
  "openToAnonymizedMeasurement",
  "consentPrivacyRequirements",
  "topic",
  "commercePlatform",
  "activeClients",
  "channelFocus",
  "organizationType",
  "programStage",
];
const key = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, "");
const topicAliases = new Set([
  "subject",
  "inquirytype",
  "enquirytype",
  "service",
  "interest",
]);

export function readEnquiryFields(form: {
  elements: ArrayLike<unknown>;
}): Record<string, string> | null {
  const fields: Record<string, string> = {};
  const names: Record<string, string> = {};
  for (const item of Array.from(form.elements)) {
    if (!item || typeof item !== "object") continue;
    const control = item as IdentityFormControl & {
      selectedOptions?: ArrayLike<HTMLOptionElement>;
    };
    if (control.disabled) continue;
    const type = String(control.type ?? "").toLowerCase();
    const sensitive = classifySensitiveFormControl(control);
    if (sensitive && sensitive !== "security_token" && sensitive !== "file")
      return null;
    if (
      sensitive ||
      [
        "hidden",
        "password",
        "file",
        "submit",
        "button",
        "reset",
        "image",
      ].includes(type)
    )
      continue;
    if (["checkbox", "radio"].includes(type) && control.checked !== true)
      continue;
    const values =
      type === "select-multiple"
        ? Array.from(control.selectedOptions ?? [])
            .filter(
              (option) =>
                !option.disabled &&
                !(
                  option.parentElement?.tagName === "OPTGROUP" &&
                  (option.parentElement as HTMLOptGroupElement).disabled
                ),
            )
            .map((option) => option.value)
        : [control.value];
    const value = values
      .filter((entry): entry is string => typeof entry === "string")
      .map((entry) => entry.trim())
      .filter(Boolean)
      .join(", ");
    if (!value) continue;
    if (value.length > 5000) return null;
    const declared =
      control.getAttribute?.("data-prospix-enquiry-field") ??
      String(control.name || control.id || "");
    const canonical =
      businessFields.find((field) => key(field) === key(declared)) ??
      (topicAliases.has(key(declared)) ? "topic" : undefined);
    const identity = identifyFormControlField(control);
    if (identity && ["first", "middle", "last"].includes(identity))
      names[identity] = value;
    const field =
      canonical ??
      (identity && businessFields.includes(identity) ? identity : undefined);
    if (field)
      fields[field] = fields[field] ? `${fields[field]}, ${value}` : value;
  }
  if (!fields.fullName) {
    const name = [names.first, names.middle, names.last]
      .filter(Boolean)
      .join(" ");
    if (name) fields.fullName = name;
  }
  if (Object.values(fields).some((value) => value.length > 5000)) return null;
  if (!fields.email && !fields.phone) return null;
  if (JSON.stringify(fields).length > 20000) return null;
  return fields;
}

/** An independent request-processing channel; it never grants tracking consent or creates a visitor ID. */
export function startEnquiryCapture(
  win: Window,
  options: {
    apiBase: string;
    siteKey: string;
    prepareAttribution?: (
      form: HTMLFormElement,
      submitter?: HTMLElement | null,
    ) => (() => EnquiryAttributionContext | undefined) | undefined;
  },
) {
  const endpoint = `${options.apiBase.replace(/\/+$/, "").replace(/\/v1$/, "")}/v1/pxl/enquiries/${encodeURIComponent(options.siteKey)}`;
  const storageKey = `prospix_enquiries_v1_${options.siteKey}`;
  let active = true,
    enabled = false,
    flushing = false,
    checking = false,
    policyResolved = false;
  let pending: Pending[] = [];
  const attribution = new Map<
    string,
    () => EnquiryAttributionContext | undefined
  >();
  const inFlight = new Set<string>();
  const recent = new WeakMap<
    HTMLFormElement,
    { payload: string; id: string }
  >();
  function report(status: string, receiptId?: string) {
    win.dispatchEvent(
      new CustomEvent("prospix:enquiry-status", {
        detail: { status, receiptId },
      }),
    );
  }
  function persist() {
    const retainedKeys = new Set(pending.map((item) => item.body.receiptKey));
    for (const receiptKey of attribution.keys()) {
      if (!retainedKeys.has(receiptKey)) attribution.delete(receiptKey);
    }
    try {
      if (pending.length)
        win.sessionStorage.setItem(storageKey, JSON.stringify(pending));
      else win.sessionStorage.removeItem(storageKey);
    } catch {
      report("retry_storage_unavailable");
    }
  }
  async function policy() {
    const response = await win.fetch(`${endpoint}/config`, {
      credentials: "omit",
      cache: "no-store",
      signal: AbortSignal.timeout(8000),
    });
    if (!response.ok) throw new Error("policy_unavailable");
    return (await response.json()).enabled === true;
  }
  async function send(item: Pending): Promise<"sent" | "retry" | undefined> {
    const key = item.body.receiptKey;
    if (
      !active ||
      (policyResolved && !enabled) ||
      inFlight.has(key) ||
      item.expiresAt <= Date.now()
    )
      return;
    inFlight.add(key);
    try {
      let body = JSON.stringify(item.body);
      let credentials: RequestCredentials = "omit";
      try {
        const context = attribution.get(key)?.();
        if (context) {
          const withAttribution = JSON.stringify({
            ...item.body,
            attribution: context,
          });
          // Keep the independent receipt when optional CMP evidence would
          // exceed the collector's body limit. Never truncate consent data.
          if (
            new TextEncoder().encode(withAttribution).byteLength <=
            MAX_ENQUIRY_BODY_BYTES
          ) {
            body = withAttribution;
            credentials = "include";
          }
        }
      } catch {
        // A stale/failed identity guard cannot block the business receipt.
        attribution.delete(key);
      }
      const response = await win.fetch(endpoint, {
        method: "POST",
        credentials,
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
        signal: AbortSignal.timeout(8000),
      });
      if (!active) return;
      if (response.ok) {
        const result = await response.json();
        if (!result.ok || !result.receipt?.id)
          throw new Error("missing_receipt");
        if (!active) return;
        attribution.delete(key);
        pending = pending.filter((entry) => entry !== item);
        report("recorded_attempt", result.receipt.id);
        persist();
        if (result.attribution?.status === "not_linked")
          report("attribution_not_linked", result.receipt.id);
      } else if (response.status === 403) {
        enabled = false;
        pending = [];
        persist();
        report("disabled");
      } else if (
        response.status >= 400 &&
        response.status < 500 &&
        response.status !== 429
      ) {
        pending = pending.filter((entry) => entry !== item);
        persist();
        report("rejected");
      } else {
        throw new Error("retry_later");
      }
      return "sent";
    } catch {
      report("pending_retry");
      return "retry";
    } finally {
      inFlight.delete(key);
      persist();
    }
  }
  async function flush() {
    if (!active || !enabled || flushing || !pending.length) return;
    flushing = true;
    try {
      pending = pending.filter((item) => item.expiresAt > Date.now());
      // Restored receipts stay serial to bound the browser keepalive budget.
      // New submissions take their own send path and never wait for this ACK.
      for (const item of [...pending]) {
        const outcome = await send(item);
        if (!active || !enabled || outcome === "retry") break;
      }
    } finally {
      flushing = false;
      persist();
    }
  }
  function onSubmit(event: Event) {
    if (!active) return;
    if (policyResolved && !enabled) {
      report("capture_not_ready");
      return;
    }
    const form = event.target as HTMLFormElement | null;
    if (
      !form ||
      form.tagName !== "FORM" ||
      ["off", "server"].includes(
        form.getAttribute("data-prospix-enquiry") ?? "",
      )
    )
      return;
    try {
      const submitter = (event as SubmitEvent).submitter;
      const action = submitter?.getAttribute("formaction") ?? form.action;
      const method = submitter?.getAttribute("formmethod") ?? form.method;
      // These are attempts observed on the installed site, regardless of where
      // its own form sends them. The collector still enforces the site's Origin;
      // neither an email draft nor a provider POST proves successful delivery.
      const protocol = new URL(action || win.location.href, win.location.href)
        .protocol;
      if (
        method?.toLowerCase() === "dialog" ||
        !["http:", "https:", "mailto:"].includes(protocol)
      ) {
        report("unsupported_form");
        return;
      }
      const fields = readEnquiryFields(form);
      if (!fields) {
        report("unsupported_form");
        return;
      }
      const sourcePath = win.location.pathname;
      const payload = JSON.stringify({ sourcePath, fields });
      pending = pending.filter((item) => item.expiresAt > Date.now());
      const previous = recent.get(form);
      // Suppress repeated submit events only while the same request is pending.
      // Acknowledged requests must never consume a later submission's receipt.
      if (
        previous?.payload === payload &&
        pending.some((item) => item.body.receiptKey === previous.id)
      ) {
        const queued = pending.find(
          (item) => item.body.receiptKey === previous.id,
        );
        if (queued) void send(queued);
        return;
      }
      const receiptKey = win.crypto.randomUUID();
      const body = { receiptKey, sourcePath, fields };
      // The collector caps UTF-8 bytes, not JavaScript string length. Check the
      // complete independent envelope before persisting or attempting delivery.
      if (
        new TextEncoder().encode(JSON.stringify(body)).byteLength >
        MAX_ENQUIRY_BODY_BYTES
      ) {
        report("unsupported_form");
        return;
      }
      if (pending.length >= 10) {
        report("retry_queue_full");
        return;
      }
      recent.set(form, { payload, id: receiptKey });
      try {
        const readContext = options.prepareAttribution?.(form, submitter);
        if (readContext) attribution.set(receiptKey, readContext);
      } catch {
        // Tracking readiness cannot prevent the independent business receipt.
      }
      const item = {
        body,
        expiresAt: Date.now() + 15 * 60000,
      };
      pending.push(item);
      persist();
      if (!enabled) report("pending_configuration");
      // Fetch starts synchronously before the host's native submit navigates.
      // A pending config read cannot delay an attempt; the POST enforces the
      // current active site/Origin policy. Known disabled sites already skipped.
      void send(item);
    } catch {
      report("capture_failed");
    }
  }
  // Restore once before accepting new submissions. A failed configuration
  // request must not erase an in-memory queue when storage is unavailable.
  // Delivery remains gated by a successful policy response in flush().
  try {
    const saved: unknown = JSON.parse(
      win.sessionStorage.getItem(storageKey) ?? "[]",
    );
    if (Array.isArray(saved))
      pending = saved
        .slice(0, 10)
        .filter(
          (item) =>
            item?.expiresAt > Date.now() &&
            item?.body?.receiptKey &&
            item?.body?.fields,
        )
        .map((item) => ({
          // A restored queue is only an independent business receipt. Never
          // restore attribution, visitor IDs or cookies from stored envelopes.
          body: {
            receiptKey: item.body.receiptKey,
            sourcePath: item.body.sourcePath,
            fields: item.body.fields,
          },
          expiresAt: item.expiresAt,
        }));
  } catch {
    /* Storage restrictions do not block in-memory delivery. */
  }
  function onOnline() {
    if (!policyResolved) void refreshPolicy();
    else void flush();
  }
  win.document.addEventListener("submit", onSubmit, true);
  win.addEventListener("online", onOnline);
  const timer = win.setInterval(() => {
    if (!policyResolved) void refreshPolicy();
    else void flush();
  }, 15000);
  async function refreshPolicy() {
    if (!active || checking) return;
    checking = true;
    try {
      enabled = await policy();
      policyResolved = true;
      if (!active) return;
      if (enabled) {
        report("ready");
        void flush();
      } else {
        pending = [];
        persist();
        report("disabled");
      }
    } catch {
      report("configuration_unavailable");
    } finally {
      checking = false;
    }
  }
  void refreshPolicy();
  return () => {
    active = false;
    attribution.clear();
    win.clearInterval(timer);
    win.document.removeEventListener("submit", onSubmit, true);
    win.removeEventListener("online", onOnline);
  };
}
